import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CallOffMechanism, FrameworkAgreementType, useCreateFrameworkAgreementMutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";

const MECHANISMS = [
  CallOffMechanism.DirectAward,
  CallOffMechanism.MiniCompetition,
  CallOffMechanism.DirectAwardAndMiniCompetition,
];

export const CreateFrameworkAgreementModal = ({
  eventId,
  defaultTitle,
  defaultMechanism,
  supplierCount,
  isOpen,
  onClose,
}: {
  eventId: string;
  defaultTitle: string;
  defaultMechanism: CallOffMechanism;
  supplierCount: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const [create, { isLoading }] = useCreateFrameworkAgreementMutation();
  const [title, setTitle] = useState(defaultTitle);
  const [callOffMechanism, setMechanism] = useState(defaultMechanism);

  const submit = async () => {
    try {
      const framework = await create({
        eventId,
        createFrameworkAgreementRequest: {
          title: title.trim() === "" ? undefined : title.trim(),
          callOffMechanism,
        },
      }).unwrap();
      onClose();
      navigate(urls.app.frameworkAgreements.view.go(framework.id));
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("Create framework agreement")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Text color="smMuted" fontSize="sm">
              {t("One contract is created for each of the {{count}} awarded suppliers.", { count: supplierCount })}
            </Text>
            <FormControl>
              <FormLabel htmlFor="framework-title">{t("Title")}</FormLabel>
              <Input
                id="framework-title"
                backgroundColor="smBackground"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="framework-mechanism">{t("Call-off mechanism")}</FormLabel>
              <Select
                id="framework-mechanism"
                backgroundColor="smBackground"
                value={callOffMechanism}
                onChange={(e) => setMechanism(e.target.value as CallOffMechanism)}
              >
                {MECHANISMS.map((value) => (
                  <option key={value} value={value}>
                    {t(value)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isLoading={isLoading} onClick={submit}>
            {t("Create framework agreement")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const callOffMechanismFor = (type: FrameworkAgreementType | undefined | null): CallOffMechanism => {
  switch (type) {
    case FrameworkAgreementType.WithoutReopening:
      return CallOffMechanism.DirectAward;
    case FrameworkAgreementType.Mixed:
      return CallOffMechanism.DirectAwardAndMiniCompetition;
    default:
      return CallOffMechanism.MiniCompetition;
  }
};
